'use client';

import { addToCart } from '@/api/cart';
import {
  CA_LENS_PENDING_CART_SCROLL_ID,
  getGoodsDetailAddCartScene,
} from '@/app/_lens-components/constants';
import {
  fetchBffRelatedRecommendProducts,
  fetchBffGoodsDetailRecommend,
  fetchBffGoodsDetail,
  fetchBffProductDetailGift,
} from '@/api/product';
import { IReviewsData, IReviewsItem, ProductDetailGiftPayload } from '@/api/types/product';
import { IGoodsDetail, IProductDetail } from '@/api/types/public';
import Dialog from '@/components/Dialog';
import { handleTopCarouselData } from '@/components/GoodsDetail/handleData';
import SizeGuideDialog from '@/components/GoodsDetail/SizeGuide';
import { Product } from '@/components/Product/ProductCard';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { SubscribeBar, useComplianceSubscriptionDisplay } from '@/components/Subscribe';
import { startCaFrameListDiagnostic } from '@/domain/cart/pending-lens-diagnostic/client';
import { createProductDetailGiftRequestGuard } from '@/domain/product/gift-display';
import useEffectWithCurReady from '@/hooks/useEffectWithCurReady';
import { useFirstEnter } from '@/hooks/useFirstEnter';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { DIRECTLY_ADD_TO_CART_PARAMS } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { removeProductInfo, setProductInfo } from '@/utils/feature-value';
import { setJustAddCart, setSession } from '@/utils/tools';
import { Box } from '@mui/material';
import Cookie from 'js-cookie';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { useTranslations } from '@/i18n/use-translations';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import PcGoodsDetail from '../components/PcGoodsDetail';
import WapGoodsDetail from '../components/WapGoodsDetail';
import { useGoodsDetailTrack } from '../hooks/useGoodsDetailTrack';
import { usePriceChangeDialog } from '../hooks/usePriceChangeDialog';

const Popup = dynamic(() => import('@/components/Popup'), { ssr: false });
const GuessYouLike = dynamic(() => import('@/components/GuessYouLike'), { ssr: true });
const GuessYouLikeClientOnly = dynamic(() => import('@/components/GuessYouLike'), { ssr: false });
const RelatedRecommendList = dynamic(
  () => import('@/components/GoodsDetail/RelatedRecommendList'),
  {
    ssr: true,
  },
);
const RelatedRecommendListClientOnly = dynamic(
  () => import('@/components/GoodsDetail/RelatedRecommendList'),
  { ssr: false },
);

// 接口定义
interface ServerData {
  detail: {
    product: IProductDetail;
    customer: IGoodsDetail['customer'];
    select_lens_config: IGoodsDetail['select_lens_config'];
  };
  showTabBtn: boolean;
  goodsSKU: string;
  reviewsRes: IReviewsData;
  featuredReview?: IReviewsItem;
  recommendList?: Product[];
  relatedRecommendList?: Product[];
  reviewImagesRes?: {
    data: Array<{
      image_show?: string;
      review_id?: number;
      star?: number | string;
      title?: string;
      detail?: string;
      [key: string]: any;
    }>;
    total?: number;
  };
}

interface ClientComponentProps {
  serverData: ServerData;
  isBot?: boolean;
}

// 主要组件 - 作为逻辑处理和分发器
export default function GoodsDetailClient({ serverData, isBot = false }: ClientComponentProps) {
  const router = useRouter();
  const recommendT = useTranslations('goodsDetail.recommendProducts');
  const t = useTranslations('goodsDetail');
  const currency = useStore((state) => state.currency);
  const storeToken = useStore((state) => state.token);
  const waiMode = useStore((state) => state.waiMode);
  const initialRecommendList = serverData.recommendList || [];
  const initialRelatedRecommendList = serverData.relatedRecommendList || [];
  // 主要状态
  const [detail, setDetail] = useState(serverData.detail);
  // 因为服务端渲染对接口做了缓存了，所以对于商品详情页的价格已经是不可信的了，需要重新获取一次
  const [originalDetail, setOriginalDetail] = useState(serverData.detail);
  const [loginDetail, setLoginDetail] = useState(serverData.detail);
  const [showTabBtn] = useState(serverData.showTabBtn);
  const [hideRecommendList, setHideRecommendList] = useState(
    isBot ? initialRecommendList.length === 0 : false,
  );
  const [isLoadingRecommend, setIsLoadingRecommend] = useState(false);
  const [hideRelatedRecommendList, setHideRelatedRecommendList] = useState(() => {
    if (isBot) {
      return initialRelatedRecommendList.length === 0;
    }
    const related_recommendation_id = serverData?.detail?.product?.related_recommendation_id;
    return !related_recommendation_id || Number(related_recommendation_id) === 0;
  });
  const [isLoadingRelatedRecommend, setIsLoadingRelatedRecommend] = useState(false);

  // 促销活动标签数据
  const [promotionScheduleData, setPromotionScheduleData] = useState<any>(null);
  const [giftData, setGiftData] = useState<ProductDetailGiftPayload | null>(null);
  const giftRequestGuardRef = useRef(createProductDetailGiftRequestGuard());

  // 商品详情相关状态
  const [reviewsData] = useState<IReviewsData>(serverData.reviewsRes);
  const [featuredReview] = useState<IReviewsItem | undefined>(serverData.featuredReview);
  const [recommendList, setRecommendList] = useState<Product[]>(initialRecommendList);
  const [relatedRecommendList, setRelatedRecommendList] = useState<Product[]>(
    initialRelatedRecommendList,
  );

  // 弹窗和对话框状态
  const getReviewDialogFromStore = useStore((state) => state.getReviewDialog);
  const setPartialToStore = useStore((state) => state.setPartial);
  const [sizeDialog, setSizeDialog] = useState<boolean>(false);
  const [reviewDialog, setReviewDialog] = useState<boolean>(
    getReviewDialogFromStore(serverData?.detail?.product?.shop_sku),
  );

  // 加载状态
  const [addCartLoading, setAddCartLoading] = useState<boolean>(false);
  const [frameOnlyLoading, setFrameOnlyLoading] = useState<boolean>(false);
  const [selectLensLoading, setSelectLensLoading] = useState<boolean>(false);

  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const { isShowSubscribeEmail } = useComplianceSubscriptionDisplay();

  // 因为服务端对商品详情接口和评论接口做了缓存，所以有些个性化数据会在客户端重新获取接口来渲染
  // 该标志位表示已经重新获取接口
  const clientRenderReadyRef = useRef(false);
  // 计算属性
  const product = useMemo((): IProductDetail => {
    return detail?.product;
  }, [detail]);

  const shopSkuRef = useRef(product?.shop_sku);
  const isFirstEnter = useFirstEnter();
  // TODO: 临时方案
  const initCookieTokenRef = useRef(Cookie.get('token'));
  const { ref: recommendListRef, inView: recommendInView } = useInView({
    triggerOnce: true,
    threshold: 0.01, // 当元素 1% 可见时触发
  });
  const { ref: relatedRecommendListRef, inView: relatedRecommendInView } = useInView({
    triggerOnce: true,
    threshold: 0.01, // 当元素 1% 可见时触发
  });

  useEffect(() => {
    setProductInfo({
      product_sku: product.sku,
      shop_sku: product.shop_sku,
    });
    return () => {
      removeProductInfo();
    };
  }, [product]);

  useEffect(() => {
    shopSkuRef.current = product?.shop_sku;
  }, [product?.shop_sku]);

  // 从 detail.label.march_spring_promotion_schedule 获取促销活动标签数据
  const fetchPromotionScheduleData = useCallback(() => {
    try {
      const promotionData = (detail as any)?.label?.march_spring_promotion_schedule;
      if (promotionData) {
        // 检查 show_status，1展示，2不展示
        if (promotionData?.show_status === 1) {
          setPromotionScheduleData(promotionData);
        } else {
          setPromotionScheduleData(null);
        }
      } else {
        setPromotionScheduleData(null);
      }
    } catch (error) {
      logger.log('Failed to fetch promotion schedule data:', error);
      setPromotionScheduleData(null);
    }
  }, [detail]);

  useEffect(() => {
    fetchPromotionScheduleData();
  }, [fetchPromotionScheduleData]);

  const fetchProductDetailGift = useCallback(async () => {
    const requestId = giftRequestGuardRef.current.start();

    if (!product?.shop_sku) {
      setGiftData(null);
      return;
    }

    setGiftData(null);

    try {
      const response = await fetchBffProductDetailGift(product.shop_sku, currency.name);
      if (!giftRequestGuardRef.current.isCurrent(requestId)) return;

      if (response.code === 200 && response.data?.show) {
        setGiftData(response.data);
        return;
      }
      setGiftData(null);
    } catch (error) {
      if (!giftRequestGuardRef.current.isCurrent(requestId)) return;

      logger.error('获取商品详情赠品失败:', error);
      setGiftData(null);
    }
  }, [currency.name, product?.shop_sku]);

  useEffectWithCurReady(() => {
    if (isBot) return;
    fetchProductDetailGift();
  }, [fetchProductDetailGift, isBot, storeToken]);

  // 当商品切换时重置推荐列表状态
  useEffect(() => {
    const currentShopSku = product?.shop_sku;
    if (shopSkuRef.current !== currentShopSku) {
      // 重置推荐列表相关状态
      setRecommendList([]);
      setHideRecommendList(false);
      setIsLoadingRecommend(false);
      // 重置相关推荐列表状态
      setRelatedRecommendList([]);
      // 检查是否有 related_recommendation_id
      const related_recommendation_id = product?.related_recommendation_id;
      if (!related_recommendation_id || Number(related_recommendation_id) === 0) {
        setHideRelatedRecommendList(true);
      } else {
        setHideRelatedRecommendList(false);
      }
      setIsLoadingRelatedRecommend(false);
    }
  }, [product?.shop_sku, product?.related_recommendation_id]);

  // 获取推荐商品
  const getRecommendList = useCallback(
    async (isFirst?: boolean) => {
      if (!product?.shop_sku || isLoadingRecommend) return;

      setIsLoadingRecommend(true);

      try {
        const response = await fetchBffGoodsDetailRecommend(
          {
            shop_sku: product.shop_sku,
          },
          isWeb,
        );
        if (response.code === 200) {
          setRecommendList((response.data as any) || []);
          if (isFirst && (!response.data || response.data.length === 0)) {
            setHideRecommendList(true);
          }
        } else {
          // API 返回非 200 状态码
          logger.error('获取推荐商品失败: API 返回错误', response);
          if (isFirst) {
            // 如果是首次加载失败，隐藏推荐区域
            setHideRecommendList(true);
          }
        }
      } catch (error) {
        logger.error('获取推荐商品失败:', error);
        if (isFirst) {
          // 如果是首次加载失败，隐藏推荐区域
          setHideRecommendList(true);
        }
      } finally {
        setIsLoadingRecommend(false);
      }
    },
    [product, isLoadingRecommend],
  );

  // 当推荐区域进入视口时加载推荐数据
  useEffect(() => {
    if (isBot) {
      return;
    }

    if (
      recommendInView &&
      recommendList.length === 0 &&
      !hideRecommendList &&
      !isLoadingRecommend &&
      product?.shop_sku
    ) {
      getRecommendList(true);
    }
  }, [
    recommendInView,
    recommendList.length,
    hideRecommendList,
    isLoadingRecommend,
    isBot,
    product?.shop_sku,
    getRecommendList,
  ]);

  // 评论打开后，使缓存失效，避免在其他路由回退时误恢复
  useEffect(() => {
    if (reviewDialog) {
      setPartialToStore({ reviewDialog: false });
    }
  }, [reviewDialog]);

  const shapePath = useMemo(() => {
    const { product_frame_shape, nav_shape_group_id } = product;
    if (nav_shape_group_id) {
      return {
        id: nav_shape_group_id.toString(),
        name: product_frame_shape,
      };
    }
    return null;
  }, [product]);

  const materialPath = useMemo(() => {
    const { product_frame_material, nav_material_group_id } = product;
    if (nav_material_group_id) {
      return {
        id: nav_material_group_id.toString(),
        name: product_frame_material,
      };
    }
    return null;
  }, [product]);

  // 生成眼镜过滤词
  const getGlassesFilter = useMemo(() => {
    try {
      let word = '';
      if (product?.short_name) {
        if (product.is_web_glass_classification === 1) {
          word = `${t('Glasses')}`;
        }
        if (product.is_web_sunglasses_classification === 1) {
          word = `${t('Sunglasses')}`;
        }
      }
      return word;
    } catch {
      return '';
    }
  }, [product]);

  const {
    SD_TryONBtn_Exp,
    SDAddToCartBtnClick,
    SDAddToCartResult,
    SDProductDetailsPageBtnClick,
    SDProductDetailsPageInteractionBtnClick,
    SDProductDetailsPageRelatedMktExp,
    SDProductDetailsPageRelatedMktClick,
    SDProductDetailsPageProductPicExp,
    SDProductDetailsPageYouAlsoLikeMktValidExp,
    SDProductDetailsPageYouAlsoLikeMktClick,
    sendThirdPartyAddToCart,
    sdProductDetailsPageComplementMktValidExp,
    sdProductDetailsComplementMktClick,
  } = useGoodsDetailTrack({
    product,
    reviewsData,
    isWeb,
    isWap: deviceType === EPlatform.WAP,
  });

  // 使用价格变动弹窗 Hook
  usePriceChangeDialog(originalDetail.product, loginDetail.product);

  // 获取相关推荐商品
  const getRelatedRecommendList = useCallback(
    async (isFirst?: boolean) => {
      const related_recommendation_id = product?.related_recommendation_id;
      if (
        !related_recommendation_id ||
        Number(related_recommendation_id) === 0 ||
        isLoadingRelatedRecommend
      ) {
        if (isFirst) {
          setHideRelatedRecommendList(true);
        }
        return;
      }

      setIsLoadingRelatedRecommend(true);

      try {
        const { code, data } = await fetchBffRelatedRecommendProducts(
          {
            related_recommendation_id,
          },
          isWeb,
        );
        if (code === 200) {
          setRelatedRecommendList(data?.list || []);
          if (isFirst && (!data?.list || data.list.length === 0)) {
            setHideRelatedRecommendList(true);
          }
        } else {
          // API 返回非 200 状态码
          logger.error('获取相关推荐商品失败: API 返回错误', code);
          if (isFirst) {
            // 如果是首次加载失败，隐藏推荐区域
            setHideRelatedRecommendList(true);
          }
        }
      } catch (error) {
        logger.error('获取相关推荐商品失败:', error);
        if (isFirst) {
          // 如果是首次加载失败，隐藏推荐区域
          setHideRelatedRecommendList(true);
        }
      } finally {
        setIsLoadingRelatedRecommend(false);
      }
    },
    [product, isLoadingRelatedRecommend],
  );

  // 当相关推荐区域进入视口时加载相关推荐数据
  useEffect(() => {
    if (isBot) {
      return;
    }

    if (
      relatedRecommendInView &&
      relatedRecommendList.length === 0 &&
      !hideRelatedRecommendList &&
      !isLoadingRelatedRecommend &&
      product?.related_recommendation_id
    ) {
      getRelatedRecommendList(true);
    }
  }, [
    relatedRecommendInView,
    relatedRecommendList.length,
    hideRelatedRecommendList,
    isLoadingRelatedRecommend,
    isBot,
    product?.related_recommendation_id,
    getRelatedRecommendList,
  ]);

  // 刷新页面数据
  const refreshPageData = async (isShouldRefreshRecommendData = true) => {
    try {
      if (!product?.shop_sku) return;
      const { code, data } = await fetchBffGoodsDetail(product.shop_sku, isWeb);
      if (code === 200) {
        // 处理轮播图数据
        const handledData = handleTopCarouselData(data.product);
        const { productDetail } = handledData;
        const _detail = {
          ...data,
          product: productDetail,
          customer: data.customer || { is_wish: 0, is_new: 'N' },
          select_lens_config: data.select_lens_config,
        };
        setDetail(_detail);
        setLoginDetail(_detail);
        if (!clientRenderReadyRef.current) {
          clientRenderReadyRef.current = true;
          setOriginalDetail(_detail);
        }

        if (isShouldRefreshRecommendData) {
          // 并行请求推荐数据
          await Promise.all([getRecommendList(), getRelatedRecommendList()]);
        }
      } else {
        throw new Error('Failed to fetch product detail');
      }
    } catch (error) {
      logger.error('刷新商品详情失败:', error);
      // 可以添加错误提示
    }
  };

  // 监听登录状态变化，重新获取商品详情
  useEffectWithCurReady(() => {
    // 避免首次调用
    if (isFirstEnter.current) return;
    // token 未变化则不处理
    if (storeToken === initCookieTokenRef.current) return;
    // 只在商品详情页面加载后再监听token变化（product数据来自服务端，不需要判断sku是否存在）
    if (detail && Object.keys(detail).length > 0) {
      refreshPageData();
    }
  }, [storeToken]);

  // ISR 缓存场景：服务端用站点默认货币渲染，非 bot 用户首次挂载时 re-fetch 获取用户个性化数据
  useEffectWithCurReady(() => {
    if (!isBot) {
      refreshPageData(true);
    }
  }, [isBot]);

  // 监听路由变化，清理状态
  useEffect(() => {
    return () => {
      // 组件卸载时重置所有状态
      setFrameOnlyLoading(false);
      setSelectLensLoading(false);
      setAddCartLoading(false);
    };
  }, []);

  // 加购处理
  const directAddToCart = async (isPendingLens = false) => {
    const addCartScene = getGoodsDetailAddCartScene(isPendingLens);
    try {
      SDAddToCartBtnClick(addCartScene);
      setAddCartLoading(true);
      const option = {
        ...DIRECTLY_ADD_TO_CART_PARAMS,
        ...(isPendingLens ? { is_pending_lens: 1 as const } : {}),
      };
      if (product.is_glass === 1) {
        option.prescription_type = 'Frame Only';
      }
      const addToCartParams = {
        currency: currency.name,
        product_id: product.id,
        qty: 1,
        option,
        origin_option: [],
      };
      const res = await addToCart(addToCartParams);
      SDAddToCartResult(`${res.code}`, res.code === 200, res.msg, addCartScene);
      if (res.code === 200) {
        setJustAddCart([res.data.cart_id]);
        if (isPendingLens) {
          startCaFrameListDiagnostic(res.data.cart_id);
          sessionStorage.setItem(CA_LENS_PENDING_CART_SCROLL_ID, String(res.data.cart_id));
        }
        setSession('zl-sensors-entrance-cart-scene', '商详页直接加购');
        sendThirdPartyAddToCart();
        sessionStorage.setItem('toApp', '1');
        router.push('/cart'); // 跳转购物车
      }
    } catch (error) {
      SDAddToCartResult(`${error.code}`, false, error.msg, addCartScene);
      logger.error('加购失败:', error);
    } finally {
      setAddCartLoading(false);
    }
  };

  // 选择镜片
  const selectLens = useCallback(async () => {
    setSelectLensLoading(true);
    setPartialToStore({ reviewDialog, shop_sku: product.shop_sku });
    router.push(`/lens-process?sku=${product.shop_sku}`);
    sessionStorage.setItem('zl-sensors-lens-entrance-scene', `商详页进入|Select Lenses`);
  }, [product.shop_sku, router, reviewDialog, setPartialToStore]);

  // 框架
  const frameOnly = useCallback(async () => {
    setFrameOnlyLoading(true);
    setPartialToStore({ reviewDialog, shop_sku: product.shop_sku });
    router.push(`/frame-process?sku=${product.shop_sku}`);
    sessionStorage.setItem('zl-sensors-lens-entrance-scene', `商详页进入|Frame Only`);
  }, [product.shop_sku, router, reviewDialog, setPartialToStore]);

  // 尺寸指南
  const handleShowSizeGuide = useCallback(() => {
    setSizeDialog(true);
  }, []);

  // 显示评论详情对话框
  const handleViewMoreReviews = useCallback(() => {
    setReviewDialog(true);
  }, []);

  // 底部按钮点击处理
  const bottomButtonClick = (type: number, btnName: string) => {
    if (type === 0) directAddToCart();
    else if (type === 1) selectLens();
    else if (type === 2) frameOnly();
    else if (type === 3) directAddToCart(true);
  };

  // 推荐商品点击事件 - 包装原有方法并添加场景设置
  const handleRecommendMktClick = (params: any, listData: any[]) => {
    setSession('zl_goodsDetailEnterPageScene', '商品详情页猜你喜欢商品坑位');
    SDProductDetailsPageYouAlsoLikeMktClick(params, listData);
  };

  const breadcrumbItems = useMemo(() => {
    const home = { label: t('home'), href: '/' };
    const shape = {
      label: `${shapePath?.name ? `${shapePath?.name} ${t('glasses')}` : ''}`,
      href: `/goods-list/${shapePath?.id}`,
      onClick: () => {
        setSession('zl_goodsListEnterPageScene', '商品详情页面包屑导航');
        router.push(`/goods-list/${shapePath?.id}`);
      },
    };
    const glasses = { label: `${product.short_name}${getGlassesFilter}`, href: '' };
    if (!shapePath) {
      return [home, glasses];
    }
    return [home, shape, glasses];
  }, [shapePath, product, getGlassesFilter]);

  // 传递给子组件的props（不包含推荐商品相关）
  const sharedProps = {
    refreshPageData,
    detail,
    showTabBtn,
    reviewsData,
    reviewImagesRes: serverData.reviewImagesRes,
    shapePath,
    materialPath,
    getGlassesFilter,
    sizeDialog,
    setSizeDialog,
    reviewDialog,
    setReviewDialog,
    addCartLoading,
    frameOnlyLoading,
    selectLensLoading,
    handleShowSizeGuide,
    handleViewMoreReviews,
    bottomButtonClick,
    breadcrumbItems,
    SD_TryONBtn_Exp,
    SDProductDetailsPageBtnClick,
    SDProductDetailsPageInteractionBtnClick,
    SDProductDetailsPageRelatedMktExp,
    SDProductDetailsPageRelatedMktClick,
    SDProductDetailsPageProductPicExp,
    clientRenderReady: clientRenderReadyRef.current,
    promotionScheduleData,
    giftData,
    isBot,
  };

  return (
    <>
      {/* PC端组件 */}
      <WebOnly>
        <PcGoodsDetail {...sharedProps} />
      </WebOnly>

      {/* WAP端组件 */}
      <WapOnly>
        <WapGoodsDetail {...sharedProps} featuredReview={featuredReview} />
      </WapOnly>

      <WapOnly>
        {isShowSubscribeEmail && (
          <div className="bg-[#f5f5f5] pt-[10px]">
            <SubscribeBar background="#fff" className="!h-[60px] !rounded-none !px-[28px] !py-0" />
          </div>
        )}
      </WapOnly>

      <WebOnly>
        {isShowSubscribeEmail && (
          <div className="mt-48 mx-[45px]">
            <SubscribeBar />
          </div>
        )}
      </WebOnly>

      {/* 推荐商品 - 统一处理 */}
      {!hideRecommendList && (
        <>
          <WapOnly>
            <div ref={recommendListRef} className="relative pb-20">
              {isBot ? (
                <GuessYouLike
                  renderTitle={() => (
                    <>
                      <div className="h-[10px] bg-auxiliary-gray" />
                      <h3 className="text-20 font-bb text-primary-text pt-[34px] pb-16 text-center">
                        {/* You may also like */}
                        {recommendT('title')}
                      </h3>
                    </>
                  )}
                  onValidMktExp={SDProductDetailsPageYouAlsoLikeMktValidExp}
                  onMktClick={handleRecommendMktClick}
                  list={recommendList}
                  showSkeleton
                  showCorrelation
                  showCorrelationCount={3}
                />
              ) : (
                <GuessYouLikeClientOnly
                  renderTitle={() => (
                    <>
                      <div className="h-[10px] bg-auxiliary-gray" />
                      <h3 className="text-20 font-bb text-primary-text pt-[34px] pb-16 text-center">
                        {/* You may also like */}
                        {recommendT('title')}
                      </h3>
                    </>
                  )}
                  onValidMktExp={SDProductDetailsPageYouAlsoLikeMktValidExp}
                  onMktClick={handleRecommendMktClick}
                  list={recommendList}
                  showSkeleton
                  showCorrelation
                  showCorrelationCount={3}
                />
              )}
              <h3 className="flex items-center justify-center absolute bottom-24 left-0 right-0">
                <p className="w-40 h-1 bg-auxiliary-dark-gray" />
                <span
                  className={`text-12 ${
                    waiMode ? 'text-primary-black' : 'text-auxiliary-dark-gray'
                  } mx-8`}
                >
                  {recommendT('noMore')}
                </span>
                <p className="w-40 h-1 bg-auxiliary-dark-gray" />
              </h3>
            </div>
          </WapOnly>
          <WebOnly>
            <Box ref={recommendListRef} className="py-[60px]">
              {isBot ? (
                <GuessYouLike
                  renderTitle={() => (
                    <h3 className="text-24 font-bb text-primary-text pb-[2vw] text-center">
                      {recommendT('title')}
                    </h3>
                  )}
                  list={recommendList}
                  showSkeleton
                  showCorrelation
                  showCorrelationCount={6}
                  divideCount={4}
                  canSwipe
                  autoPlay
                  onValidMktExp={SDProductDetailsPageYouAlsoLikeMktValidExp}
                  onMktClick={handleRecommendMktClick}
                />
              ) : (
                <GuessYouLikeClientOnly
                  renderTitle={() => (
                    <h3 className="text-24 font-bb text-primary-text pb-[2vw] text-center">
                      {recommendT('title')}
                    </h3>
                  )}
                  list={recommendList}
                  showSkeleton
                  showCorrelation
                  showCorrelationCount={6}
                  divideCount={4}
                  canSwipe
                  autoPlay
                  onValidMktExp={SDProductDetailsPageYouAlsoLikeMktValidExp}
                  onMktClick={handleRecommendMktClick}
                />
              )}
            </Box>
          </WebOnly>
        </>
      )}

      {/* 相关推荐商品 - 统一处理 */}
      {!hideRelatedRecommendList && (
        <div ref={relatedRecommendListRef}>
          {isBot ? (
            <RelatedRecommendList
              recommendList={relatedRecommendList}
              isWeb={isWeb}
              showSkeleton
              sdProductDetailsComplementMktClick={sdProductDetailsComplementMktClick}
              sdProductDetailsPageComplementMktValidExp={sdProductDetailsPageComplementMktValidExp}
            />
          ) : (
            <RelatedRecommendListClientOnly
              recommendList={relatedRecommendList}
              isWeb={isWeb}
              showSkeleton
              sdProductDetailsComplementMktClick={sdProductDetailsComplementMktClick}
              sdProductDetailsPageComplementMktValidExp={sdProductDetailsPageComplementMktValidExp}
            />
          )}
        </div>
      )}
      <WebOnly>
        <Dialog.Info
          open={sizeDialog}
          onClose={() => setSizeDialog(false)}
          title={t('sizeChart')}
          sx={{
            '& .MuiDialog-paper': {
              maxWidth: '680px',
            },
          }}
        >
          <SizeGuideDialog
            detail={product}
            isWeb
            SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
          />
        </Dialog.Info>
      </WebOnly>
      {/* 尺寸指南弹窗 - 统一处理 */}
      <WapOnly>
        <Popup
          open={sizeDialog}
          onClose={() => setSizeDialog(false)}
          title={t('sizeChart')}
          height="70%"
          position="bottom"
        >
          <SizeGuideDialog
            detail={product}
            isWeb={false}
            SDProductDetailsPageBtnClick={SDProductDetailsPageBtnClick}
          />
        </Popup>
      </WapOnly>
    </>
  );
}
